import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Send, Image as ImageIcon } from 'lucide-react';
import { FileUploader } from '@/components/FileUploader';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardCampaigns() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', message: '', type: 'both', target_customers: 'all', image_url: '' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadCampaigns();
  }, [navigate]);

  const loadCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(response.data);
    } catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleCreateCampaign = async () => {
    if (!formData.title || !formData.message) { toast.error('Compila tutti i campi'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/campaigns`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === 'en' ? 'Campaign created' : 'Campagna creata');
      setDialogOpen(false);
      setFormData({ title: '', message: '', type: 'both', target_customers: 'all', image_url: '' });
      loadCampaigns();
    } catch (error) { toast.error('Errore nella creazione'); }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm(lang === 'en' ? 'Send this campaign to all customers?' : 'Inviare questa campagna a tutti i clienti?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/campaigns/${campaignId}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      loadCampaigns();
    } catch (error) { toast.error("Errore nell'invio"); }
  };

  const dateLocale = lang === 'en' ? enUS : it;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-campaigns">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {lang === 'en' ? 'Campaign Management' : 'Gestione Campagne'}
              </h1>
              <p className="text-gray-600">{lang === 'en' ? 'Create and send marketing campaigns via WhatsApp and Email' : 'Crea e invia campagne marketing via WhatsApp ed Email'}</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0066b2] hover:bg-[#005494]" data-testid="create-campaign-button">
                  <Plus className="w-5 h-5 mr-2" /> {lang === 'en' ? 'New Campaign' : 'Nuova Campagna'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="campaign-dialog">
                <DialogHeader>
                  <DialogTitle>{lang === 'en' ? 'Create New Campaign' : 'Crea Nuova Campagna'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{lang === 'en' ? 'Title' : 'Titolo'}</label>
                    <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="campaign-title-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{lang === 'en' ? 'Message' : 'Messaggio'}</label>
                    <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={6} data-testid="campaign-message-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{lang === 'en' ? 'Image' : 'Immagine'}</label>
                    <FileUploader accept="image/*" label={lang === 'en' ? 'Upload image' : 'Carica immagine'} onUploadComplete={(url) => setFormData({ ...formData, image_url: url })} />
                    {formData.image_url && <img src={formData.image_url} alt="Preview" className="mt-2 h-24 w-auto rounded-lg object-cover" />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{lang === 'en' ? 'Type' : 'Tipo'}</label>
                    <select className="w-full p-2 border border-gray-300 rounded-lg" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} data-testid="campaign-type-select">
                      <option value="both">WhatsApp + Email</option>
                      <option value="whatsapp">{lang === 'en' ? 'WhatsApp Only' : 'Solo WhatsApp'}</option>
                      <option value="email">{lang === 'en' ? 'Email Only' : 'Solo Email'}</option>
                    </select>
                  </div>
                  <Button onClick={handleCreateCampaign} className="w-full bg-[#0066b2] hover:bg-[#005494]" data-testid="save-campaign-button">
                    {lang === 'en' ? 'Create Campaign' : 'Crea Campagna'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12"><div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="campaigns-loading" /></div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="campaigns-empty">{lang === 'en' ? 'No campaigns created' : 'Nessuna campagna creata'}</div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign, idx) => (
                <div key={campaign.id} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-testid={`campaign-item-${idx}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1 flex gap-4">
                      {campaign.image_url && <img src={campaign.image_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{campaign.title}</h3>
                        <p className="text-gray-600 mb-2 line-clamp-2">{campaign.message}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className={`px-3 py-1 rounded-full ${campaign.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {campaign.status === 'sent' ? (lang === 'en' ? 'Sent' : 'Inviata') : (lang === 'en' ? 'Draft' : 'Bozza')}
                          </span>
                          <span>{campaign.type}</span>
                          <span>{format(new Date(campaign.created_at), 'dd MMM yyyy', { locale: dateLocale })}</span>
                        </div>
                      </div>
                    </div>
                    {campaign.status === 'draft' && (
                      <Button onClick={() => handleSendCampaign(campaign.id)} className="bg-green-600 hover:bg-green-700 ml-4" data-testid={`send-campaign-${idx}`}>
                        <Send className="w-4 h-4 mr-2" /> {lang === 'en' ? 'Send' : 'Invia'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
